import Person from '@mui/icons-material/Person';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Alert,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  Home,
  Payment,
  Description,
  Build,
  Receipt,
  CalendarMonth,
  Notifications,
  ContactPhone,
  Upload,
  Download,
  Euro,
  Warning,
  CheckCircle,
  Schedule,
  FileUpload,
  PictureAsPdf,
  Assignment,
  LocalPhone,
  Email,
  WhatsApp,
  ReceiptLong,
  WaterDrop,
  Bolt,
  LocalFireDepartment,
  ArrowForward,
  InsertDriveFile
} from '@mui/icons-material';
import axios from 'axios';

function TenantPortal() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenantData, setTenantData] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  
  // Données simulées du locataire
  const mockTenantData = {
    apartment: 'A23',
    tenant: {
      firstName: 'Jean',
      lastName: 'Dupont',
      email: 'jean.dupont@email.com',
      phone: '0692 12 34 56'
    },
    rent: {
      amount: 850,
      charges: 150,
      total: 1000,
      dueDate: '05',
      nextDue: '05/10/2025',
      status: 'paid', // paid, pending, late
      balance: 0
    },
    documents: {
      lease: { name: 'Contrat de location.pdf', date: '01/01/2024', status: 'valid' },
      insurance: { name: 'Attestation assurance.pdf', date: '15/08/2024', status: 'valid', expires: '14/08/2025' },
      inventory: { name: 'État des lieux.pdf', date: '01/01/2024' },
      rules: { name: 'Règlement intérieur.pdf', date: '01/01/2024' }
    },
    payments: [
      { month: 'Septembre 2025', amount: 1000, status: 'paid', date: '05/09/2025' },
      { month: 'Août 2025', amount: 1000, status: 'paid', date: '05/08/2025' },
      { month: 'Juillet 2025', amount: 1000, status: 'paid', date: '05/07/2025' }
    ],
    interventions: {
      pending: 2,
      inProgress: 1,
      completed: 5
    },
    contacts: {
      manager: { name: 'Marie Martin', phone: '0262 12 34 56', email: 'gestion@residence.re' },
      emergency: { name: 'Urgences 24/7', phone: '0262 99 99 99' },
      concierge: { name: 'Paul Bernard', phone: '0692 45 67 89' }
    },
    utilities: {
      electricity: { provider: 'EDF', reference: 'EDF123456' },
      water: { provider: 'Véolia', reference: 'VEO789012' }
    }
  };

  useEffect(() => {
    // Simuler le chargement des données
    setTimeout(() => {
      setTenantData(mockTenantData);
      setLoading(false);
    }, 1000);
  }, []);

  const handlePayRent = () => {
    setPaymentDialog(true);
  };

  const handleDownloadDocument = (docType) => {
    console.log('Téléchargement:', docType);
    // Simuler le téléchargement
    alert(`Téléchargement de ${mockTenantData.documents[docType].name}`);
  };

  const handleUploadDocument = (docType) => {
    setSelectedService(docType);
    setUploadDialog(true);
  };

  const handleSignalProblem = () => {
    // Rediriger vers le formulaire de signalement avec auth auto
    navigate(`/scan/${code}?action=report`);
  };

  const getRentStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'late': return 'error';
      default: return 'default';
    }
  };

  const getRentStatusLabel = (status) => {
    switch(status) {
      case 'paid': return 'Payé';
      case 'pending': return 'En attente';
      case 'late': return 'En retard';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Chargement de votre espace...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      {/* En-tête */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 56, height: 56 }}>
              <Home fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h4">
                Mon Espace Locataire
              </Typography>
              <Typography variant="body1">
                Appartement {tenantData.apartment} - {tenantData.tenant.firstName} {tenantData.tenant.lastName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6">
              Résidence Les Palmiers
            </Typography>
            <Typography variant="body2">
              Saint-Denis 97400
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Section Loyer */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  <Euro sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Mon Loyer
                </Typography>
                <Chip 
                  label={getRentStatusLabel(tenantData.rent.status)}
                  color={getRentStatusColor(tenantData.rent.status)}
                />
              </Box>
              
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Loyer</Typography>
                    <Typography variant="h6">{tenantData.rent.amount}€</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Charges</Typography>
                    <Typography variant="h6">{tenantData.rent.charges}€</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" fontWeight="bold">Total mensuel</Typography>
                      <Typography variant="h5" color="primary">{tenantData.rent.total}€</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Typography variant="body2" color="textSecondary" gutterBottom>
                Prochaine échéance: {tenantData.rent.nextDue}
              </Typography>

              <Button 
                variant="contained" 
                fullWidth 
                size="large"
                onClick={handlePayRent}
                startIcon={<Payment />}
                disabled={tenantData.rent.status === 'paid'}
              >
                {tenantData.rent.status === 'paid' ? 'Loyer payé' : 'Payer mon loyer'}
              </Button>

              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ mt: 1 }}
                startIcon={<ReceiptLong />}
              >
                Voir l'historique des paiements
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Section Interventions */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Build sx={{ verticalAlign: 'middle', mr: 1 }} />
                Mes Interventions
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {tenantData.interventions.pending}
                    </Typography>
                    <Typography variant="caption">En attente</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {tenantData.interventions.inProgress}
                    </Typography>
                    <Typography variant="caption">En cours</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {tenantData.interventions.completed}
                    </Typography>
                    <Typography variant="caption">Terminées</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Button 
                variant="contained" 
                fullWidth 
                color="error"
                size="large"
                onClick={handleSignalProblem}
                startIcon={<Warning />}
              >
                Signaler un problème
              </Button>

              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ mt: 1 }}
                onClick={() => navigate('/interventions')}
              >
                Voir toutes mes demandes
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Section Documents */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Description sx={{ verticalAlign: 'middle', mr: 1 }} />
                Mes Documents
              </Typography>

              <Grid container spacing={2}>
                {/* Contrat de location */}
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Assignment color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">Contrat de location</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {tenantData.documents.lease.date}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button 
                        size="small" 
                        startIcon={<Download />}
                        onClick={() => handleDownloadDocument('lease')}
                      >
                        Télécharger
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                {/* Attestation assurance */}
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle color="success" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">Assurance habitation</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Expire le {tenantData.documents.insurance.expires}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button 
                        size="small" 
                        startIcon={<Upload />}
                        onClick={() => handleUploadDocument('insurance')}
                      >
                        Mettre à jour
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                {/* État des lieux */}
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Home color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">État des lieux</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {tenantData.documents.inventory.date}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button 
                        size="small" 
                        startIcon={<Download />}
                        onClick={() => handleDownloadDocument('inventory')}
                      >
                        Télécharger
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                {/* Règlement intérieur */}
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InsertDriveFile color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">Règlement intérieur</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Version actuelle
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button 
                        size="small" 
                        startIcon={<Download />}
                        onClick={() => handleDownloadDocument('rules')}
                      >
                        Télécharger
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  💡 Pensez à renouveler votre attestation d'assurance avant le {tenantData.documents.insurance.expires}
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Contacts utiles */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <ContactPhone sx={{ verticalAlign: 'middle', mr: 1 }} />
                Contacts Utiles
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  <ListItemText 
                    primary={tenantData.contacts.manager.name}
                    secondary="Gestionnaire"
                  />
                  <Box>
                    <IconButton size="small" color="primary">
                      <LocalPhone />
                    </IconButton>
                    <IconButton size="small" color="primary">
                      <Email />
                    </IconButton>
                  </Box>
                </ListItem>

                <Divider />

                <ListItem>
                  <ListItemIcon>
                    <Warning color="error" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={tenantData.contacts.emergency.name}
                    secondary={tenantData.contacts.emergency.phone}
                  />
                  <Button 
                    variant="contained" 
                    color="error" 
                    size="small"
                    startIcon={<LocalPhone />}
                  >
                    Appeler
                  </Button>
                </ListItem>

                <Divider />

                <ListItem>
                  <ListItemIcon>
                    <Home />
                  </ListItemIcon>
                  <ListItemText 
                    primary={tenantData.contacts.concierge.name}
                    secondary="Concierge"
                  />
                  <IconButton size="small" color="success">
                    <WhatsApp />
                  </IconButton>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Références compteurs */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Receipt sx={{ verticalAlign: 'middle', mr: 1 }} />
                Mes Références
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <Bolt color="warning" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={tenantData.utilities.electricity.provider}
                    secondary={`Référence: ${tenantData.utilities.electricity.reference}`}
                  />
                  <Button size="small" endIcon={<ArrowForward />}>
                    Relever
                  </Button>
                </ListItem>

                <Divider />

                <ListItem>
                  <ListItemIcon>
                    <WaterDrop color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={tenantData.utilities.water.provider}
                    secondary={`Référence: ${tenantData.utilities.water.reference}`}
                  />
                  <Button size="small" endIcon={<ArrowForward />}>
                    Relever
                  </Button>
                </ListItem>
              </List>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Prochain relevé des compteurs: 15/10/2025
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog paiement */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Paiement du loyer</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" gutterBottom>
              Montant à payer: {tenantData.rent.total}€
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Loyer + charges pour {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Button variant="contained" fullWidth sx={{ mb: 1 }}>
                Payer par carte bancaire
              </Button>
              <Button variant="outlined" fullWidth sx={{ mb: 1 }}>
                Virement bancaire
              </Button>
              <Button variant="outlined" fullWidth>
                Prélèvement automatique
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Annuler</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog upload document */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mettre à jour le document</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Button variant="outlined" component="label" fullWidth>
              <FileUpload sx={{ mr: 1 }} />
              Choisir un fichier PDF
              <input type="file" hidden accept=".pdf" />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Annuler</Button>
          <Button variant="contained">Envoyer</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TenantPortal;
